import { useTranslation } from 'react-i18next';
import { formatGetal } from '../format';
import { weeknummer } from '../week';

export interface DoelbalkProps {
  /** Het hoofdcijfer van de week, een heel percentage van de dienst. */
  percentage: number;
  /** Het geldende doel (`afbakening.doelInEenKeerGoed`); zonder doel geen doelstreep. */
  doel?: number;
  /** De week ervoor, alleen met een percentage; zonder geen streepje. */
  vorige?: { weekKey: string; percentage: number };
}

/** Tot en met de helft staat een label rechts van zijn streep, daarboven links: zo valt het nooit buiten de balk. */
const HELFT = 100 / 2;
const anker = (procent: number) => (procent <= HELFT ? 'start' : 'end');
const procent = (waarde: number) => `${Math.min(100, Math.max(0, waarde))}%`;

/**
 * Eén streep (doel of vorige week): een halo in de achtergrondkleur van 4px eronder, en de streep zelf van
 * 2px die boven en onder de balk uitsteekt (DESIGN.md Doelbalk: zichtbaar op de vulling én op het spoor).
 * De halo is een `rect` en geen `line`, met een verschuiving in pixels vanaf een plek in procenten.
 */
function Streep({ x, className }: { x: string; className: string }) {
  return (
    <>
      <rect className="fill-[var(--background)]" height="100%" transform="translate(-2 0)" width="4" x={x} y="0" />
      <line className={className} strokeWidth="2" x1={x} x2={x} y1="0" y2="100%" />
    </>
  );
}

/**
 * De Doelbalk van het Resultaatblok (en straks de Teamzin): spoor, vulling tot het cijfer, een streep op het
 * doel en een streepje voor vorige week (EXPERIENCE.md Doelbalk).
 *
 * Een SVG zonder `viewBox`, in procentcoördinaten: elke hele procent valt op zijn plek (48% naast een doel
 * van 50% blijft zichtbaar onder de streep) zonder inline stijl. Het beeld is `role="img"` met het hele
 * verhaal als label; de zichtbare labels zijn `aria-hidden`. Doel boven, vorige week onder, zodat ze elkaar
 * niet raken.
 */
export function Doelbalk({ percentage, doel, vorige }: DoelbalkProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const getal = (waarde: number) => formatGetal(waarde, locale);
  const heeftDoel = typeof doel === 'number';
  const vorigeWeek = vorige === undefined ? null : weeknummer(vorige.weekKey);

  const aria = [
    t('qa.rapportkaart.doelbalkAria', { percentage: getal(percentage) }),
    vorige !== undefined && t('qa.rapportkaart.doelbalkAriaVorige', { week: vorigeWeek, percentage: getal(vorige.percentage) }),
    heeftDoel && t('qa.rapportkaart.doelbalkAriaDoel', { doel: getal(doel) }),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="pb-5 pt-5">
      <svg aria-label={aria} className="block h-5 w-full overflow-visible" overflow="visible" role="img" width="100%">
        <rect className="fill-[var(--border)] stroke-[var(--border-strong)]" height="60%" rx="6" width="100%" x="0" y="20%" />
        <rect className="fill-[var(--chart-current)]" height="60%" rx="6" width={procent(percentage)} x="0" y="20%" />
        {vorige !== undefined && <Streep className="stroke-[var(--foreground-muted)]" x={procent(vorige.percentage)} />}
        {heeftDoel && <Streep className="stroke-[var(--chart-goal)]" x={procent(doel)} />}
        <g aria-hidden="true" className="text-[0.75rem]">
          {heeftDoel && (
            <text className="fill-[var(--foreground)] font-medium" dy="-0.4em" textAnchor={anker(doel)} x={procent(doel)} y="0">
              {t('qa.rapportkaart.doelbalkDoel', { doel: getal(doel) })}
            </text>
          )}
          {vorige !== undefined && (
            <text className="fill-[var(--foreground-muted)]" dy="1.1em" textAnchor={anker(vorige.percentage)} x={procent(vorige.percentage)} y="100%">
              {t('qa.rapportkaart.doelbalkVorige', { week: vorigeWeek, percentage: getal(vorige.percentage) })}
            </text>
          )}
        </g>
      </svg>
    </div>
  );
}
